import { lazy, createElement } from "react";
import { defineConfig } from "sanity";
import { structureTool } from "sanity/structure";
import { presentationTool, defineDocuments, defineLocations } from "sanity/presentation";

import { schemaTypes } from "./sanity/schemaTypes";

const EmailTool = lazy(() => import("./sanity/tools/EmailTool"));
const CatalogTool = lazy(() => import("./sanity/tools/CatalogTool"));
const ClientsTool = lazy(() => import("./sanity/tools/ClientsTool"));
const InvoicesTool = lazy(() => import("./sanity/tools/InvoicesTool"));
const HomePageTool = lazy(() => import("./sanity/tools/HomePageTool"));
const TestimonialsTool = lazy(() => import("./sanity/tools/TestimonialsTool"));

const projectId =
  process.env.SANITY_STUDIO_PROJECT_ID || process.env.NEXT_PUBLIC_SANITY_PROJECT_ID;
const dataset =
  process.env.SANITY_STUDIO_DATASET || process.env.NEXT_PUBLIC_SANITY_DATASET;
const apiVersion = process.env.NEXT_PUBLIC_SANITY_API_VERSION || "2025-01-01";

if (!projectId) {
  throw new Error("Missing SANITY_STUDIO_PROJECT_ID / NEXT_PUBLIC_SANITY_PROJECT_ID env var");
}
if (!dataset) {
  throw new Error("Missing SANITY_STUDIO_DATASET / NEXT_PUBLIC_SANITY_DATASET env var");
}

const singletonTypes = new Set(["siteSettings", "homePage", "aboutPage", "shopPage", "contactPage", "returnsPage", "jewelryCarePage", "categoryImages", "customRequestPage", "eventsPage", "inStorePage", "bagPage", "favoritesPage", "accountPage", "itemBullets"]);
const hiddenTypes = new Set([...singletonTypes, "emailTemplate", "mailingGroup", "emailLog", "clientCarePage", "blogPost"]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const singleton = (S: any, title: string, type: string) =>
  S.listItem()
    .title(title)
    .id(type)
    .child(S.document().schemaType(type).documentId(type));

// Document → URL for Presentation: shows a "used on this page / open in
// Presentation" link in the document editor for each fixed-route singleton.
// This is the reverse of mainDocuments (URL → document) below.
const pageLocation = (title: string, href: string) =>
  defineLocations({
    select: {},
    resolve: () => ({
      message: `Used on ${href}`,
      tone: "positive",
      locations: [{ title, href }]
    })
  });

export default defineConfig({
  name: "default",
  title: "Vault88 Studio",
  projectId,
  dataset,
  apiVersion,
  basePath: "/studio",
  auth: {
    // Force token-only auth so Sanity always writes the session token to
    // localStorage. Default `dual` mode uses cookies when available (Chrome),
    // which leaves no token for our /api/admin routes to forward to Sanity.
    loginMethod: "token",
  },
  tools: (prev) => [
    // Drop Sanity's built-in "Releases" tab — this studio does not use content
    // releases, and the tab only adds noise to the nav. Sanity supports this:
    // its own useReleasesToolAvailable() checks the workspace tool list rather
    // than the feature flag, precisely so a filtered-out tool degrades cleanly.
    ...prev.filter((tool) => tool.name !== "releases"),
    {
      name: "home-page",
      title: "Home Page",
      component: HomePageTool,
      icon: () =>
        createElement(
          "svg",
          { width: "1em", height: "1em", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" },
          createElement("path", { d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" }),
          createElement("polyline", { points: "9 22 9 12 15 12 15 22" })
        ),
    },
    {
      name: "catalog",
      title: "Catalog",
      component: CatalogTool,
      icon: () =>
        createElement(
          "svg",
          { width: "1em", height: "1em", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" },
          createElement("rect", { x: 3, y: 3, width: 7, height: 7, rx: 1 }),
          createElement("rect", { x: 14, y: 3, width: 7, height: 7, rx: 1 }),
          createElement("rect", { x: 3, y: 14, width: 7, height: 7, rx: 1 }),
          createElement("rect", { x: 14, y: 14, width: 7, height: 7, rx: 1 })
        ),
    },
    {
      name: "email",
      title: "Email",
      component: EmailTool,
      icon: () =>
        createElement(
          "svg",
          { width: "1em", height: "1em", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" },
          createElement("rect", { x: 2, y: 4, width: 20, height: 16, rx: 2 }),
          createElement("path", { d: "M22 7L13.03 12.7a1.94 1.94 0 01-2.06 0L2 7" })
        ),
    },
    {
      name: "clients",
      title: "Clients",
      component: ClientsTool,
      icon: () =>
        createElement(
          "svg",
          { width: "1em", height: "1em", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" },
          // two people silhouette
          createElement("path", { d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" }),
          createElement("circle", { cx: 9, cy: 7, r: 4 }),
          createElement("path", { d: "M23 21v-2a4 4 0 0 0-3-3.87" }),
          createElement("path", { d: "M16 3.13a4 4 0 0 1 0 7.75" })
        ),
    },
    {
      name: "invoices",
      title: "Invoices",
      component: InvoicesTool,
      icon: () =>
        createElement(
          "svg",
          { width: "1em", height: "1em", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" },
          // receipt
          createElement("path", { d: "M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" }),
          createElement("path", { d: "M16 8H8" }),
          createElement("path", { d: "M16 12H8" }),
          createElement("path", { d: "M13 16H8" })
        ),
    },
    {
      name: "testimonials",
      title: "Testimonials",
      component: TestimonialsTool,
      icon: () =>
        createElement(
          "svg",
          { width: "1em", height: "1em", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" },
          createElement("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
        ),
    },
  ],
  plugins: [
    presentationTool({
      previewUrl: {
        origin: typeof window !== "undefined" ? window.location.origin : "/",
        preview: "/",
        previewMode: {
          enable: "/api/draft/enable",
          disable: "/api/draft/disable"
        }
      },
      // Map URL → primary singleton so "Documents on this page" updates as
      // the iframe navigates. siteSettings shows up automatically via the
      // stega tags rendered into every layout.
      resolve: {
        mainDocuments: defineDocuments([
          { route: "/", filter: `_type == "homePage"` },
          { route: "/about", filter: `_type == "aboutPage"` },
          { route: "/browse", filter: `_type == "shopPage"` },
          { route: "/contact", filter: `_type == "contactPage"` },
          { route: "/events", filter: `_type == "eventsPage"` },
          { route: "/in-store", filter: `_type == "inStorePage"` },
          { route: "/cart", filter: `_type == "bagPage"` },
          { route: "/favorites", filter: `_type == "favoritesPage"` },
          { route: "/account", filter: `_type == "accountPage"` },
          { route: "/custom-request", filter: `_type == "customRequestPage"` },
          { route: "/custom", filter: `_type == "customRequestPage"` },
          { route: "/caring-for-your-pieces", filter: `_type == "jewelryCarePage"` },
          { route: "/client-care/returns", filter: `_type == "returnsPage"` },
          { route: "/client-care/:slug", filter: `_type == "clientCarePage" && slug.current == $slug` }
        ]),
        // URL → document is mainDocuments above; this is the reverse, so each
        // page document links to its live URL inside Presentation.
        locations: {
          homePage: pageLocation("Home", "/"),
          aboutPage: pageLocation("About", "/about"),
          shopPage: pageLocation("Shop", "/browse"),
          contactPage: pageLocation("Contact", "/contact"),
          eventsPage: pageLocation("Events", "/events"),
          inStorePage: pageLocation("In Store", "/in-store"),
          bagPage: pageLocation("Bag", "/cart"),
          favoritesPage: pageLocation("Favorites", "/favorites"),
          accountPage: pageLocation("Account", "/account"),
          customRequestPage: pageLocation("Custom Request", "/custom-request"),
          jewelryCarePage: pageLocation("Jewelry Care", "/caring-for-your-pieces"),
          returnsPage: pageLocation("Returns & Exchanges", "/client-care/returns"),
          clientCarePage: defineLocations({
            select: { title: "title", slug: "slug.current" },
            resolve: (doc) => ({
              locations: doc?.slug
                ? [{ title: (doc.title as string) || "Client Care", href: `/client-care/${doc.slug}` }]
                : []
            })
          })
        }
      }
    }),
    structureTool({
      structure: (S) =>
        S.list()
          .title("Content")
          .items([
            // ── Global ─────────────────────────────────────────────────
            singleton(S, "Site Settings", "siteSettings"),

            S.divider(),

            // ── Pages ──────────────────────────────────────────────────
            singleton(S, "Home", "homePage"),
            singleton(S, "About", "aboutPage"),
            singleton(S, "Shop", "shopPage"),
            singleton(S, "Contact", "contactPage"),
            singleton(S, "Events", "eventsPage"),
            singleton(S, "In Store", "inStorePage"),
            singleton(S, "Bag", "bagPage"),
            singleton(S, "Favorites", "favoritesPage"),
            singleton(S, "Account", "accountPage"),
            singleton(S, "Custom Request", "customRequestPage"),

            S.divider(),

            // ── Client Care ────────────────────────────────────────────
            singleton(S, "Returns & Exchanges", "returnsPage"),
            singleton(S, "Jewelry Care", "jewelryCarePage"),
            S.listItem()
              .title("Client Care Pages")
              .id("clientCarePages")
              .child(
                S.documentTypeList("clientCarePage")
                  .title("Client Care Pages")
              ),

            S.divider(),

            // ── Product Details ────────────────────────────────────────
            singleton(S, "Category Images", "categoryImages"),
            singleton(S, "Item Bullets", "itemBullets"),

            S.divider(),

            // Show any other document types not explicitly handled
            ...S.documentTypeListItems().filter((item) => !hiddenTypes.has(item.getId() ?? ""))
          ])
    }),
    // Dedicated top-level "Blog" tab (separate from Structure), so posts are
    // created/edited from their own tab like Clients/Testimonials.
    structureTool({
      name: "blog",
      title: "Blog",
      icon: () =>
        createElement(
          "svg",
          { width: "1em", height: "1em", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" },
          createElement("path", { d: "M4 19.5A2.5 2.5 0 0 1 6.5 17H20" }),
          createElement("path", { d: "M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" })
        ),
      structure: (S) =>
        S.documentTypeList("blogPost")
          .title("Blog")
          .defaultOrdering([{ field: "publishedAt", direction: "desc" }]),
    })
  ],
  schema: {
    types: schemaTypes,
    templates: (prev) => prev
  },
  document: {
    newDocumentOptions: (prev) =>
      prev.filter(
        (template) => !singletonTypes.has(template.templateId)
      ),
    actions: (prev, { schemaType }) => {
      if (singletonTypes.has(schemaType)) {
        return prev.filter((action) => action.action !== "delete" && action.action !== "duplicate");
      }
      return prev;
    }
  }
});
